/* eslint-disable @typescript-eslint/indent */

/* Note: please be careful with indentation, we have the rule disabled because of
*  eslint not being able to properly indent js inside styled components
*/

/* Note: .tag-selection-modal overrides the AntD portal positioning so this
 * modal renders as an overlay inside the Trendings ModalPanel instead of
 * covering the full screen.
 */

// Libraries
import styled, { createGlobalStyle } from 'styled-components';

// Config
import { BREAKPOINTS } from '../../../../../../config/screen';

// Plugins
import { hexToRgba } from '../../../../../../plugins/general';
import { getSeriesThemeColor } from '../../../../../../plugins/trendings';

/* *************************************** COMPONENTS ***************************************** */

const SeriesChipsContainerBase = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    flex-wrap: wrap;
    flex: initial;
    overflow-x: visible;
  };
`;

export const TagSelectionModalGlobalStyle = createGlobalStyle`
  .tag-selection-modal {
    .ant-modal-mask,
    .ant-modal-wrap {
      position: absolute !important;
    };
  };
`;

export const TagSelectionModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    gap: 16px;
    height: auto;
  };
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-start;
`;

export const ModalColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
`;

export const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CategoryTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 13px;
  font-family: ${({ theme }) => theme.font.bold};
  color: ${({ theme }) => theme.color.crusherDashboard.trendings.lastUpdateTextColor};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const SensorGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SensorItem = styled.div<{
  $isChecked: boolean;
  $isDisabled: boolean;
  $sensorColor: string;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background-color: ${({ theme, $isChecked, $sensorColor }) => {
    if ($isChecked) {
      const hexColor = getSeriesThemeColor($sensorColor, theme);
      return hexToRgba(hexColor, 0.1);
    }
    return theme.color.tag.neutralTypeBackgroundColor;
  }};
  opacity: ${({ $isDisabled, $isChecked }) => ($isDisabled && !$isChecked ? 0.5 : 1)};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};

  /* Override AntD default primary color for this sensor's checkbox */
  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: ${({
      theme,
      $sensorColor,
    }) => getSeriesThemeColor($sensorColor, theme)} !important;
  };

  .ant-checkbox-checked .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner,
  .ant-checkbox-checked:hover .ant-checkbox-inner {
    background-color: ${({
      theme,
      $sensorColor,
    }) => getSeriesThemeColor($sensorColor, theme)} !important;
    border-color: ${({
      theme,
      $sensorColor,
    }) => getSeriesThemeColor($sensorColor, theme)} !important;
  };
`;

export const ModalFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.color.lineChart.yAxisColor};
  padding-top: 0.75rem;
  padding-bottom: 0.25rem;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding-top: 1rem;
    padding-bottom: 0;
    margin-top: 0.5rem;
  };
`;

export const SelectionCount = styled.span`
  font-size: 14px;
  font-family: ${({ theme }) => theme.font.bold};
  color: ${({ theme }) => theme.color.crusherDashboard.trendings.lastUpdateTextColor};
`;

export const SelectionHelpText = styled.span`
  display: none;
  font-size: 12px;
  font-family: ${({ theme }) => theme.font.regular};
  color: ${({ theme }) => theme.color.crusherDashboard.trendings.lastUpdateTextColor};
  opacity: 0.6;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    display: inline;
  };
`;

export const SelectionCountContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 4px;
`;

export const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  justify-content: flex-end;
`;

export const LimitMessage = styled.div`
  flex-shrink: 0;
  font-size: 12px;
  font-family: ${({ theme }) => theme.font.regular};
  color: ${({ theme }) => theme.color.alert.warningText};
  padding: 0.5rem 0 0 0;
`;

export const ChipsContainer = styled(SeriesChipsContainerBase)`
  /* Hide the scrollbar track that browsers render at the right edge of
   * overflow-x: auto containers — the gray "|" artifact next to the Apply button */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  };

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding: 0.5rem 0;
  };
`;

export const DisabledSensorIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.alert.warningText};

  > div {
    display: flex;
    align-items: center;
  };
`;

export const SensorLabel = styled.span<{ $isDisabled: boolean }>`
  font-size: 14px;
  font-family: ${({ theme }) => theme.font.regular};
  color: ${({ theme }) => theme.color.crusherDashboard.trendings.lastUpdateTextColor};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
`;

/* *************************************** MOBILE LAYOUT *************************************** */

export const MobileLayout = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  overflow: hidden;
`;

export const MobileCategoryPanel = styled.div`
  width: 38%;
  flex-shrink: 0;
  overflow-y: auto;
  box-shadow: 2px 0 6px -2px ${({
    theme,
  }) => hexToRgba(theme.color.crusherDashboard.trendings.lastUpdateTextColor, 0.12)};
  z-index: 1;
`;

export const MobileCategoryItem = styled.div<{ $isActive: boolean }>`
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-size: 13px;
  font-family: ${({ theme, $isActive }) => (
    $isActive ? theme.font.bold : theme.font.regular
  )};
  color: ${({ theme }) => theme.color.crusherDashboard.trendings.lastUpdateTextColor};
  background-color: ${({ theme, $isActive }) => (
    $isActive ? theme.color.tag.neutralTypeBackgroundColor : 'transparent'
  )};
  border-left: 3px solid ${({ theme, $isActive }) => (
    $isActive
      ? theme.color.button.primaryType.primaryColor.backgroundColor
      : 'transparent'
  )};
  transition: background-color 0.15s ease;

  &:active {
    opacity: 0.7;
  };
`;

export const MobileSensorPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0.75rem;
`;
